// Product setup in the browser: a product's readiness (GET /api/products/:id/readiness),
// activating and retiring it, and the new-product flow's steps, checks and the
// one body it sends (POST /api/products/setup). The rules themselves live in
// the database (ops.product_readiness); the flow only checks what it must to
// send a well-formed product, and asks the server what is still missing.
// Free of React, so it can be unit tested (productSetup.test.ts).

import type { StatusStep } from '@olytic/foundation';

// ── Readiness ─────────────────────────────────────────────────────────────

/** Where a missing item is fixed: a record page tab (and a new-product step). */
export type ReadinessFix = 'details' | 'pricing' | 'billing' | 'bundle' | 'delivery';

export interface ReadinessItem {
  item: string | null;
  label: string;
  blocking: boolean;
  fix: ReadinessFix | null;
}

export interface Readiness {
  ready: boolean;
  items: ReadinessItem[];
  status?: string;
}

const FIXES = new Set<string>(['details', 'pricing', 'billing', 'bundle', 'delivery']);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** A readiness payload (also the body of a 409 from activating); anything malformed reads as not ready. */
export function readReadiness(payload: unknown): Readiness {
  if (!isRecord(payload) || !Array.isArray(payload.items)) return { ready: false, items: [] };
  const items = payload.items.filter(isRecord).map((raw): ReadinessItem => ({
    item: typeof raw.item === 'string' ? raw.item : null,
    label: typeof raw.label === 'string' ? raw.label : 'Something is missing',
    blocking: raw.blocking === true,
    fix: typeof raw.fix === 'string' && FIXES.has(raw.fix) ? (raw.fix as ReadinessFix) : null,
  }));
  return {
    ready: payload.ready === true && !items.some((i) => i.blocking),
    items,
    ...(typeof payload.status === 'string' ? { status: payload.status } : {}),
  };
}

/**
 * The checklist as StatusTimeline steps: blocking items blocked, warnings
 * upcoming, and a done step once nothing blocks ("Ready to sell" when nothing
 * is missing at all). `detail` adds each item's fix link.
 */
export function readinessSteps(
  readiness: Readiness,
  detail: (item: ReadinessItem) => StatusStep['detail'] = () => undefined,
): StatusStep[] {
  const blocking = readiness.items.filter((i) => i.blocking);
  const warnings = readiness.items.filter((i) => !i.blocking);
  const step = (item: ReadinessItem, index: number, state: StatusStep['state']): StatusStep => {
    const more = detail(item);
    return { key: item.item ?? `item-${index}`, label: item.label, state, ...(more !== undefined ? { detail: more } : {}) };
  };
  return [
    ...blocking.map((item, i) => step(item, i, 'blocked')),
    ...(blocking.length === 0
      ? [{
          key: 'ready',
          label: warnings.length === 0 ? 'Ready to sell' : 'Nothing blocks activation',
          state: 'done' as const,
          detail: warnings.length === 0 ? 'Everything it needs is in place.' : 'Worth finishing before it is quoted:',
        }]
      : []),
    ...warnings.map((item, i) => step(item, blocking.length + i, 'upcoming')),
  ];
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** A refused request: its message, and the readiness a 409 carries. */
export class ProductSetupError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly readiness: Readiness | null = null,
  ) {
    super(message);
    this.name = 'ProductSetupError';
  }
}

async function send(fetcher: Fetch, url: string, init: RequestInit, fallback: string): Promise<unknown> {
  const response = await fetcher(url, init);
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    const message = isRecord(payload) && typeof payload.error === 'string' && payload.error !== '' ? payload.error : `${fallback} (${response.status}).`;
    throw new ProductSetupError(response.status, message, response.status === 409 && isRecord(payload) && Array.isArray(payload.items) ? readReadiness(payload) : null);
  }
  return payload;
}

const post = (body?: unknown): RequestInit => ({
  method: 'POST',
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body ?? {}),
});
const productUrl = (id: string, action: string) => `/api/products/${encodeURIComponent(id)}/${action}`;

export async function loadReadiness(fetcher: Fetch, id: string, signal?: AbortSignal): Promise<Readiness> {
  return readReadiness(await send(fetcher, productUrl(id, 'readiness'), signal ? { signal } : {}, 'Could not load the checklist'));
}

/** Activates a product; a ProductSetupError with the readiness when something blocks it. */
export async function activateProduct(fetcher: Fetch, id: string): Promise<Readiness> {
  return readReadiness(await send(fetcher, productUrl(id, 'activate'), post(), 'Could not activate'));
}

export async function retireProduct(fetcher: Fetch, id: string): Promise<Readiness> {
  return readReadiness(await send(fetcher, productUrl(id, 'retire'), post(), 'Could not retire'));
}

// ── The new-product flow ──────────────────────────────────────────────────

export interface SetupOptions {
  groups: { id: string; code: string; name: string; family: string }[];
  selling_models: { id: number; name: string; kind: string; term_months: number | null }[];
  billing_policies: { id: string; name: string; description: string | null; default_treatment: string | null }[];
  price_grids: { id: string; name: string; prices_by: string | null }[];
  resource_roles: { id: string; name: string; description: string | null }[];
  products: { id: string; code: string; name: string; is_component: boolean }[];
}

export const EMPTY_OPTIONS: SetupOptions = {
  groups: [], selling_models: [], billing_policies: [], price_grids: [], resource_roles: [], products: [],
};

const listOf = <T>(value: unknown): T[] => (Array.isArray(value) ? (value.filter(isRecord) as T[]) : []);

export function readOptions(payload: unknown): SetupOptions {
  if (!isRecord(payload)) return EMPTY_OPTIONS;
  return {
    groups: listOf(payload.groups),
    selling_models: listOf(payload.selling_models),
    billing_policies: listOf(payload.billing_policies),
    price_grids: listOf(payload.price_grids),
    resource_roles: listOf(payload.resource_roles),
    products: listOf(payload.products),
  };
}

export async function loadSetupOptions(fetcher: Fetch, signal?: AbortSignal): Promise<SetupOptions> {
  return readOptions(await send(fetcher, '/api/products/setup', signal ? { signal } : {}, 'Could not load what a product is made from'));
}

/** How a price charges: flat per unit, per hour, from a Price Grid, or included at no charge. */
export type PriceScheme = 'flat' | 'per_hour' | 'grid' | 'included';
export const PRICE_SCHEMES: readonly PriceScheme[] = ['flat', 'per_hour', 'grid', 'included'];
export const SENIORITIES = ['junior', 'mid', 'senior'] as const;
export const SIZES = ['XS', 'S', 'M', 'L', 'XL'] as const;
/** The hours each size usually means, as a hint; the hours are set on their own. */
export const SIZE_HOURS: Readonly<Record<(typeof SIZES)[number], string>> = {
  XS: 'up to 2 h', S: '3–5 h', M: '6–9 h', L: '10–18 h', XL: '19 h or more',
};

export interface DraftOption {
  option_product_id: string;
  option_group: string;
  required: boolean;
  included: boolean;
  default_quantity: string;
}

export type ProductStructure = 'single' | 'bundle';
export type ProductType = 'hardware' | 'software' | 'services';
export const PRODUCT_TYPES: readonly ProductType[] = ['hardware', 'software', 'services'];
/** A product's type by its group's family, as the database defaults it (0068): software, else services. */
export const typeForFamily = (family: string | null | undefined): ProductType | '' =>
  family === undefined || family === null ? '' : family === 'software' ? 'software' : 'services';

/** One way it is sold, as the form holds it (the amount as typed). */
export interface DraftPrice {
  selling_model_id: number | null;
  scheme: PriceScheme;
  /** What a recurring price is per; ignored for a one-time selling model. */
  period: 'month' | 'year';
  amount: string;
  price_grid_id: string;
  label: string;
  is_default: boolean;
}

/** A new price row: flat, the default when it is the first. */
export const newPrice = (first: boolean, sellingModelId: number | null = null): DraftPrice => ({
  selling_model_id: sellingModelId, scheme: 'flat', period: 'month', amount: '', price_grid_id: '', label: '', is_default: first,
});

/** Everything the flow has gathered, as the form holds it (numbers as typed). */
export interface ProductDraft {
  code: string;
  name: string;
  group_id: string;
  description: string;
  structure: ProductStructure;
  /** Hardware, software or services; '' until a group is chosen (it defaults from the group's family). */
  type: ProductType | '';
  is_component: boolean;
  estimated_hours: string;
  seniority: string;
  size: string;
  qbo_item_id: string;
  /** Its prices: how it is sold. Exactly one is the default. */
  prices: DraftPrice[];
  billing_policy_id: string;
  options: DraftOption[];
  /** Share of hours by role id. */
  roles: Record<string, string>;
}

export const EMPTY_DRAFT: ProductDraft = {
  code: '', name: '', group_id: '', description: '', structure: 'single', type: '', is_component: false,
  estimated_hours: '', seniority: '', size: '', qbo_item_id: '',
  prices: [newPrice(true)], billing_policy_id: '',
  options: [], roles: {},
};

export type StepKey = 'basics' | 'prices' | 'components' | 'billing' | 'delivery' | 'review';

/** Which new-product step fixes each readiness item's `fix`. */
export const STEP_FOR_FIX: Readonly<Record<ReadinessFix, StepKey>> = {
  details: 'basics',
  pricing: 'prices',
  billing: 'billing',
  bundle: 'components',
  delivery: 'delivery',
};

const familyOf = (draft: ProductDraft, options: SetupOptions) => options.groups.find((g) => g.id === draft.group_id)?.family ?? null;

/** The default price, or the first. */
export const defaultPrice = (draft: ProductDraft): DraftPrice | undefined => draft.prices.find((p) => p.is_default) ?? draft.prices[0];

/** Service work (consulting, Studio and AI agents, unless included) says who staffs it; a bundle does through its components. */
export function needsDelivery(draft: ProductDraft, options: SetupOptions): boolean {
  const family = familyOf(draft, options);
  return (family === 'consulting' || family === 'studio' || family === 'agents' || family === 'shared') && defaultPrice(draft)?.scheme !== 'included' && draft.structure !== 'bundle';
}

/** Single service work (consulting, Studio and AI agents, unless included) carries an internal estimate: hours and a size. */
export const needsEstimate = (draft: ProductDraft, options: SetupOptions): boolean => needsDelivery(draft, options);

/**
 * The flow's steps for this product: every product has prices (a bundle that
 * rolls up its components is included at no charge), only a bundle has
 * components, components skip billing (they are billed through their
 * bundle), and only service work has delivery roles.
 */
export function stepKeys(draft: ProductDraft, options: SetupOptions): StepKey[] {
  return [
    'basics',
    'prices',
    ...(draft.structure === 'bundle' ? (['components'] as const) : []),
    ...(draft.is_component ? [] : (['billing'] as const)),
    ...(needsDelivery(draft, options) ? (['delivery'] as const) : []),
    'review',
  ];
}

const NUMBER = /^\d+(\.\d+)?$/;
const isNumber = (value: string) => NUMBER.test(value.trim());

/** Basics: the fields every product needs. */
export function checkBasics(draft: ProductDraft): string | null {
  if (draft.code.trim() === '') return 'Give the product a code.';
  if (draft.name.trim() === '') return 'Give the product a name.';
  if (draft.group_id === '') return 'Choose its product group.';
  if (draft.is_component && draft.structure === 'bundle') return 'A component cannot itself be a bundle.';
  if (!isNumber(draft.estimated_hours)) return 'Give its estimated hours (0 when it takes none).';
  return null;
}

/**
 * Prices: at least one, each on a selling model, a flat or per-hour price
 * with its amount, a Price Grid price with its grid, exactly one the default,
 * and each selling model once.
 */
export function checkPrices(draft: ProductDraft): string | null {
  if (draft.prices.length === 0) return 'Give it at least one price.';
  for (const [i, price] of draft.prices.entries()) {
    const which = `Price ${i + 1}`;
    if (price.selling_model_id === null) return `${which}: choose how it is sold.`;
    if ((price.scheme === 'flat' || price.scheme === 'per_hour') && !isNumber(price.amount)) return `${which}: give its amount.`;
    if (price.scheme === 'grid' && price.price_grid_id === '') return `${which}: choose its Price Grid.`;
  }
  if (draft.prices.filter((p) => p.is_default).length !== 1) return 'Mark exactly one price as the default.';
  const models = draft.prices.map((p) => p.selling_model_id);
  if (new Set(models).size !== models.length) return 'Give each selling model one price.';
  return null;
}

export function checkComponents(draft: ProductDraft): string | null {
  if (draft.options.some((o) => o.option_product_id === '')) return 'Choose a product for every component, or remove it.';
  if (new Set(draft.options.map((o) => o.option_product_id)).size !== draft.options.length) return 'Add each component once.';
  if (draft.options.some((o) => o.default_quantity.trim() !== '' && !isNumber(o.default_quantity))) return 'A default quantity must be a number.';
  return null;
}

/** The roles' shares, to the hundredth. */
export function roleTotal(draft: ProductDraft): number {
  const total = Object.values(draft.roles).reduce((sum, v) => sum + (isNumber(v) ? Number(v) : 0), 0);
  return Math.round(total * 100) / 100;
}

/** Delivery: shares are numbers up to 100 and, once any is given, total 100%. */
export function checkDelivery(draft: ProductDraft): string | null {
  const given = Object.values(draft.roles).filter((v) => v.trim() !== '');
  if (given.some((v) => !isNumber(v) || Number(v) <= 0 || Number(v) > 100)) return 'Each share is a percentage above 0 and up to 100.';
  if (given.length > 0 && roleTotal(draft) !== 100) return `The shares total ${roleTotal(draft)}%; they must total 100%.`;
  return null;
}

const blankToNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());
const numberOrNull = (value: string): number | null => (isNumber(value) ? Number(value) : null);

/** The body of POST /api/products/setup (and its readiness preview) for what the flow holds. */
export function setupBody(draft: ProductDraft, options: SetupOptions, activate: boolean): Record<string, unknown> {
  const steps = new Set(stepKeys(draft, options));
  return {
    product: {
      code: draft.code.trim(),
      name: draft.name.trim(),
      group_id: draft.group_id,
      description: blankToNull(draft.description),
      structure: draft.structure,
      type: draft.type === '' ? null : draft.type,
      is_component: draft.is_component,
      estimated_hours: numberOrNull(draft.estimated_hours),
      seniority: needsEstimate(draft, options) && draft.seniority !== '' ? draft.seniority : null,
      size: needsEstimate(draft, options) && draft.size !== '' ? draft.size : null,
      billing_policy_id: steps.has('billing') && draft.billing_policy_id !== '' ? draft.billing_policy_id : null,
      qbo_item_id: steps.has('billing') ? blankToNull(draft.qbo_item_id) : null,
    },
    prices: draft.prices.map((p) => ({
      selling_model_id: p.selling_model_id,
      scheme: p.scheme,
      period: options.selling_models.find((m) => m.id === p.selling_model_id)?.kind === 'one_time' ? null : p.period,
      unit_price: p.scheme === 'flat' || p.scheme === 'per_hour' ? numberOrNull(p.amount) : 0,
      price_grid_id: p.scheme === 'grid' && p.price_grid_id !== '' ? p.price_grid_id : null,
      label: blankToNull(p.label),
      is_default: p.is_default,
    })),
    options: steps.has('components')
      ? draft.options.map((o) => ({
          option_product_id: o.option_product_id,
          option_group: blankToNull(o.option_group),
          required: o.required,
          included: o.included,
          default_quantity: numberOrNull(o.default_quantity),
        }))
      : [],
    roles: steps.has('delivery')
      ? Object.entries(draft.roles).flatMap(([id, share]) => (isNumber(share) && Number(share) > 0 ? [{ resource_role_id: id, share_pct: Number(share) }] : []))
      : [],
    activate,
  };
}

/** What the product would still need, from the server's own rules; nothing is saved. */
export async function previewReadiness(fetcher: Fetch, body: Record<string, unknown>): Promise<Readiness> {
  return readReadiness(await send(fetcher, '/api/products/setup/readiness', post(body), 'Could not check the product'));
}

/** Creates the product and everything it needs in one transaction; resolves to its id. */
export async function createProduct(fetcher: Fetch, body: Record<string, unknown>): Promise<{ id: string; readiness: Readiness }> {
  const payload = await send(fetcher, '/api/products/setup', post(body), 'Could not save the product');
  if (!isRecord(payload) || typeof payload.id !== 'string') throw new ProductSetupError(500, 'The new product came back without an id.');
  return { id: payload.id, readiness: readReadiness(payload) };
}
